import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import {
  ErrorAlert,
  Form,
  Badge,
  Modal,
  Field,
  Input,
  CONTROL_LG,
  KeyValue,
  Button,
  Section,
  Toggle,
  useToast,
  type Requirement,
} from "@/components/ui";
import {
  CONTACT_MAX,
  ROLE_LABELS,
  daysUntil,
  domainKeys,
  domainsApi,
  has,
  joinPhone,
  longDate,
  splitPhone,
  useDomainDetail,
  verificationApplies,
  verificationBadge,
  type Contact,
  type ContactChangeDetail,
  type ContactInput,
  type DomainDetail,
  type Operation,
  type OtherRole,
} from "@/lib/domains";
import { timeAgo } from "@/lib/format";
import { COUNTRIES, countryName } from "@/lib/payments";
import { useUnsavedChanges } from "@/lib/unsavedChanges";

import { TeammateModal } from "../security/TeammateModal";
import { pendingOp } from "../security/shared";
import { TwoColumns, Notice, Hint } from "@/components/domains/parts";

/** Figma "Domain Control Centre — Contacts" (2057-793), edit registrant (4090-6797), add teammate (4100-1275). */
export function ContactsTab({ detail }: { detail: DomainDetail }) {
  const d = detail.domain;
  const contact = detail.contact;
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [editOpen, setEditOpen] = useState(false);
  const manage: Requirement = { permission: "domains:manage", on: ["domain_folder", detail.folder?.id] };

  const [changeOpen, setChangeOpen] = useState(false);
  const privacyOp = pendingOp(detail, "privacy");
  const contactsOp = pendingOp(detail, "contacts");
  // A change waiting for owners' approval can take days: no need to poll for it.
  useDomainDetail(d.id, {
    poll: Boolean(privacyOp || (contactsOp && !contactsChange(contactsOp)?.awaiting_approval)),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
  const toast = useToast();
  const resend = useMutation({
    mutationFn: () => domainsApi.resendVerification(orgId, d.id),
    onSuccess: async () => {
      await refresh();
      toast.show({ tone: "success", title: "Verification email sent" });
    },
  });
  const privacy = useMutation({
    mutationFn: (on: boolean) => domainsApi.setPrivacy(orgId, d.id, on),
    onSuccess: async (_, on) => {
      await refresh();
      toast.show({
        tone: "success",
        title: on ? "Privacy protection turned on" : "Privacy protection turned off",
      });
    },
  });
  const withdraw = useMutation({
    mutationFn: (operationId: string) => domainsApi.cancelContactChange(orgId, d.id, operationId),
    onSuccess: async () => {
      await refresh();
      toast.show({ tone: "success", title: "Contact change withdrawn" });
    },
  });

  // Only some registries (ICANN's gTLDs) ask the owner to confirm their email.
  const applies = verificationApplies(contact, d.fqdn);
  const pending = applies && contact?.verification === "pending";
  const badge = contact && applies ? verificationBadge(contact) : undefined;
  const daysLeft = daysUntil(contact?.verification_due_at ?? null);
  const urgent = pending && contact?.verification_due_at;
  const closeTeammate = () =>
    setParams((p) => {
      p.delete("modal");
      return p;
    });

  const registrant = (
    <Section
      title="Registrant"
      subtitle={
        pending && contact?.verification_sent_at
          ? `Verification sent to ${contact.email} ${timeAgo(contact.verification_sent_at)}`
          : `The legal owner of record for ${d.display_name}`
      }
      action={
        contact &&
        (!applies ? (
          <Badge size="md" tone="neutral">
            Verification not required
          </Badge>
        ) : pending ? (
          <Badge size="md" tone="danger">
            Verification required
          </Badge>
        ) : (
          <Badge size="md" tone="success">
            {badge?.detail ? badge.label : "Verified"}
          </Badge>
        ))
      }
    >
      {contact ? (
        <>
          <KeyValue variant="detail" label="Full name">
            <Value>{contact.full_name}</Value>
          </KeyValue>
          {contact.organization_name && (
            <KeyValue variant="detail" label="Organization">
              <Value>{contact.organization_name}</Value>
            </KeyValue>
          )}
          <KeyValue variant="detail" label="Email">
            <Value>{contact.email}</Value>
          </KeyValue>
          <KeyValue variant="detail" label="Phone">
            <Value>{formatPhone(contact.phone)}</Value>
          </KeyValue>
          <KeyValue variant="detail" label="Address">
            <Value>{contact.address}</Value>
          </KeyValue>
          {badge?.detail && (
            <KeyValue variant="detail" label="Verification">
              <Value>{badge.detail}</Value>
            </KeyValue>
          )}
          {pending && daysLeft !== null && (
            <KeyValue variant="detail" label="Days remaining">
              <Value>{daysLeft <= 0 ? "Due today" : `${daysLeft} ${daysLeft === 1 ? "day" : "days"}`}</Value>
            </KeyValue>
          )}
          <Button
            size="lg"
            variant="secondary"
            className="self-start"
            requires={manage}
            onClick={() => setEditOpen(true)}
          >
            Edit registrant
          </Button>
        </>
      ) : (
        <p className="text-sm text-fg-subtle">
          No registrant is on record for this domain yet. It's set when the registration or transfer
          completes.
        </p>
      )}
    </Section>
  );

  // Admin, technical and billing contacts, when the domain keeps any of its own.
  const others = (detail.contact_roles ?? []).flatMap((role) => {
    const held = detail.contacts?.[role];
    return held ? [[role, held] as const] : [];
  });
  const change = contactsChange(contactsOp);
  const pendingChange = contactsOp && change && (
    <>
      <Notice
        tone="warning"
        title={
          change.owner_change
            ? "Waiting for both owners to approve the new registrant"
            : "Contact change waiting for the registry"
        }
        actions={
          <Button
            size="lg"
            variant="secondary"
            requires={manage}
            loading={withdraw.isPending}
            onClick={() => withdraw.mutate(contactsOp.id)}
          >
            Withdraw request
          </Button>
        }
      >
        {change.owner_change
          ? "The registrar has e-mailed each owner an approval request. The domain keeps its current registrant until both approve."
          : "The registry is applying the change."}
        {change.authorizations.length > 0 && (
          <ul className="mt-2 list-disc pl-5">
            {change.authorizations.map((a) => (
              <li key={a.title}>
                {a.title}: {a.emails.join(", ")} · {a.status === "new" ? "not answered yet" : a.status}
              </li>
            ))}
          </ul>
        )}
      </Notice>
      <ErrorAlert error={withdraw.error} />
    </>
  );
  const otherContacts = contact && others.length > 0 && (
    <Section
      title="Other contacts"
      subtitle="The people the registry contacts about this domain besides its owner"
      action={
        !contactsOp && (
          <Button size="lg" variant="secondary" requires={manage} onClick={() => setChangeOpen(true)}>
            Change
          </Button>
        )
      }
    >
      {others.map(([role, held]) => (
        <KeyValue key={role} label={ROLE_LABELS[role]}>
          <Value>
            {held.id === contact.id ? "Same as the registrant" : `${held.full_name} · ${held.email}`}
          </Value>
        </KeyValue>
      ))}
    </Section>
  );

  const privacySection = (
    <Section
      title="Privacy protection"
      subtitle={
        has(detail, "privacy")
          ? "Hides the registrant's name, email, phone and address from the public WHOIS directory. The registry still holds the real details, as ICANN requires."
          : `The .${d.tld} registry doesn't offer WHOIS privacy through CortexGrip.`
      }
      action={
        has(detail, "privacy") ? (
          <div className="flex items-center gap-2.5">
            {privacyOp && <span className="text-[13px] font-medium text-[#853f00]">Updating…</span>}
            <Toggle
              size="lg"
              checked={privacyOp ? Boolean(privacyOp.detail.enabled) : d.privacy}
              label="Privacy protection"
              requires={manage}
              busy={privacy.isPending}
              disabledReason={privacyOp ? "Waiting for the registry to confirm the last change" : undefined}
              onChange={(on) => privacy.mutate(on)}
            />
          </div>
        ) : (
          <Badge size="md" tone="neutral">
            Not available
          </Badge>
        )
      }
    >
      <ErrorAlert error={privacy.error} />
    </Section>
  );

  return (
    <>
      {urgent ? (
        <div className="flex flex-col gap-6">
          <Notice
            tone="danger"
            title={
              daysLeft !== null && daysLeft > 0
                ? `Confirm your registrant email within ${daysLeft} ${daysLeft === 1 ? "day" : "days"}`
                : "Confirm your registrant email today"
            }
            actions={
              <>
                {has(detail, "resend_verification") && (
                  <Button
                    variant="brand"
                    size="lg"
                    requires={manage}
                    loading={resend.isPending}
                    onClick={() => resend.mutate()}
                  >
                    Resend verification email
                  </Button>
                )}
                <Button size="lg" variant="secondary" requires={manage} onClick={() => setEditOpen(true)}>
                  Update registrant email
                </Button>
              </>
            }
          >
            ICANN policy requires verifying registrant contact details after certain changes. If not confirmed
            by {longDate(contact?.verification_due_at)}, {d.display_name} may be suspended until it is. This
            won't affect your website in the meantime.
            {resend.isSuccess && (
              <span className="mt-2 block font-medium text-[#007955]">Verification email sent.</span>
            )}
          </Notice>
          <ErrorAlert error={resend.error} />
          {pendingChange}
          {registrant}
          {otherContacts}
          {privacySection}
        </div>
      ) : (
        <TwoColumns
          main={
            <>
              {pendingChange}
              {registrant}
              {otherContacts}
              {privacySection}
            </>
          }
          side={
            <>
              {contact && !pending && (
                <Notice tone="success" title="Registrant email verified">
                  {contact.email} was confirmed
                  {contact.verified_at ? ` on ${longDate(contact.verified_at)}` : ""}. Changing it later needs
                  a new confirmation.
                </Notice>
              )}
              <Notice tone="cream" title="Why this is required">
                ICANN requires accurate, verified registrant details for every domain. Registries can suspend
                a domain whose owner can't be reached.
              </Notice>
            </>
          }
        />
      )}
      {contact && (
        <ChangeContactsModal
          key={changeOpen ? "open" : "closed"}
          open={changeOpen}
          onClose={() => setChangeOpen(false)}
          detail={detail}
        />
      )}
      {contact && (
        <EditRegistrantModal
          key={editOpen ? contact.id : "closed"}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          detail={detail}
          contact={contact}
        />
      )}
      <TeammateModal open={params.get("modal") === "teammate"} onClose={closeTeammate} />
    </>
  );
}

function contactsChange(op: Operation | undefined): ContactChangeDetail | undefined {
  return op ? (op.detail as unknown as ContactChangeDetail) : undefined;
}

/** Move the domain's admin, technical and billing contacts to other saved contacts (no approvals needed). */
function ChangeContactsModal({
  open,
  onClose,
  detail,
}: {
  open: boolean;
  onClose: () => void;
  detail: DomainDetail;
}) {
  const d = detail.domain;
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const saved = useQuery({
    queryKey: ["domains", orgId, "contacts"],
    queryFn: () => domainsApi.contacts(orgId),
    enabled: open,
  });
  const roles = detail.contact_roles ?? [];
  const [chosen, setChosen] = useState<Partial<Record<OtherRole, string>>>(() =>
    Object.fromEntries(roles.map((r) => [r, detail.contacts?.[r]?.id ?? ""])),
  );
  const changes = Object.fromEntries(
    roles.flatMap((r) => {
      const id = chosen[r];
      return id && id !== detail.contacts?.[r]?.id ? [[r, { contact_id: id }] as const] : [];
    }),
  );
  const toast = useToast();
  const save = useMutation({
    mutationFn: () => domainsApi.moveContacts(orgId, d.id, changes),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
      toast.show({ tone: "success", title: "Contacts updated" });
      onClose();
    },
  });
  const contacts = saved.data?.contacts ?? [];
  const unsaved = useUnsavedChanges(open && Object.keys(changes).length > 0 && !save.isPending, {
    ignoreSearch: true,
  });
  const close = () => unsaved.guard(onClose);
  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title="Change contacts"
        subtitle={`Who the registry contacts about ${d.display_name} besides its owner`}
        width={520}
        footer={
          <>
            <Button variant="secondary" size="lg" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="brand"
              size="lg"
              loading={save.isPending}
              disabledReason={Object.keys(changes).length === 0 ? "Change a contact to save" : undefined}
              onClick={() => save.mutate()}
            >
              Save changes
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {roles.map((role) => (
            <Field size="lg" key={role} label={ROLE_LABELS[role]}>
              {({ id }) => (
                <select
                  id={id}
                  value={chosen[role] ?? ""}
                  onChange={(e) => setChosen((c) => ({ ...c, [role]: e.target.value }))}
                  className={CONTROL_LG}
                >
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.full_name + (c.organization_name ? ` · ${c.organization_name}` : "")} · {c.email}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          ))}
          <Hint tone="success" title="No approval needed">
            Only a new registrant needs both owners to approve. Your saved contacts themselves aren&apos;t
            edited.
          </Hint>
          <ErrorAlert error={saved.error ?? save.error} />
        </div>
      </Modal>
      {unsaved.modal}
    </>
  );
}

function Value({ children }: { children: string }) {
  return <span className="min-w-0 text-sm font-medium break-words text-fg">{children}</span>;
}

/** "+44.7700900123" → "+44 7700900123" */
function formatPhone(phone: string): string {
  const { dial, number } = splitPhone(phone);
  return number ? `+${dial} ${number}` : phone;
}

type Values = Omit<ContactInput, "phone" | "organization_name" | "state"> & {
  organization_name: string;
  state: string;
  dial: string;
  number: string;
};

function toForm(c: Contact): Values {
  const { dial, number } = splitPhone(c.phone);
  return {
    first_name: c.first_name,
    last_name: c.last_name,
    organization_name: c.organization_name ?? "",
    email: c.email,
    dial,
    number,
    street: c.street,
    city: c.city,
    state: c.state ?? "",
    postcode: c.postcode,
    country: c.country,
  };
}

function EditRegistrantModal({
  open,
  onClose,
  detail,
  contact,
}: {
  open: boolean;
  onClose: () => void;
  detail: DomainDetail;
  contact: Contact;
}) {
  const d = detail.domain;
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  // What the form opened with: the contact may be refreshed behind the dialog without that being an edit.
  const [start] = useState(() => toForm(contact));
  const [form, setForm] = useState<Values>(start);
  const set = <K extends keyof Values>(key: K, value: Values[K]) => setForm((f) => ({ ...f, [key]: value }));
  const same = (a: string | null, b: string | null) =>
    (a ?? "").trim().replace(/\s+/g, " ").toLowerCase() ===
    (b ?? "").trim().replace(/\s+/g, " ").toLowerCase();
  // A new name, organisation or e-mail is a new owner: both owners approve it (a change of registrant).
  const ownerChanged = !(
    same(form.first_name, contact.first_name) &&
    same(form.last_name, contact.last_name) &&
    same(form.organization_name, contact.organization_name) &&
    same(form.email, contact.email)
  );
  const toast = useToast();
  const save = useMutation({
    mutationFn: (): Promise<unknown> => {
      const body: ContactInput = {
        first_name: form.first_name,
        last_name: form.last_name,
        organization_name: form.organization_name.trim() || null,
        email: form.email,
        phone: joinPhone(form.dial, form.number),
        street: form.street,
        city: form.city,
        state: form.state.trim() || null,
        postcode: form.postcode,
        country: form.country,
      };
      return ownerChanged
        ? domainsApi.moveContacts(orgId, d.id, { registrant: { contact: body } }, true)
        : domainsApi.updateContact(orgId, d.id, body);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
      toast.show({ tone: "success", title: "Contact details saved" });
      onClose();
    },
  });
  const required: (keyof Values)[] = [
    "first_name",
    "last_name",
    "email",
    "number",
    "street",
    "city",
    "postcode",
    "country",
  ];
  const complete = required.every((k) => form[k].trim());
  const countries = COUNTRIES.includes(form.country) ? COUNTRIES : [form.country, ...COUNTRIES];
  const emailChanged = form.email.trim().toLowerCase() !== contact.email.toLowerCase();

  const field = (
    key: keyof Values,
    label: string,
    opts: { optional?: boolean; type?: string; auto?: string } = {},
  ) => (
    <Field size="lg" label={label} optional={opts.optional} name={key} required={required.includes(key)}>
      {(control) => (
        <Input
          size="lg"
          {...control}
          type={opts.type ?? "text"}
          autoComplete={opts.auto}
          value={form[key]}
          maxLength={key in CONTACT_MAX ? CONTACT_MAX[key as keyof typeof CONTACT_MAX] : undefined}
          onChange={(e) => set(key, e.target.value)}
        />
      )}
    </Field>
  );

  const edited = (Object.keys(form) as (keyof Values)[]).some((k) => form[k] !== start[k]);
  const unsaved = useUnsavedChanges(open && edited && !save.isPending, { ignoreSearch: true });
  const close = () => unsaved.guard(onClose);

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title="Edit registrant"
        subtitle={`The legal owner of record for ${d.display_name}`}
        width={520}
        note={d.privacy ? "Kept private via WHOIS privacy protection." : undefined}
        footer={
          <>
            <Button variant="secondary" size="lg" onClick={close}>
              Cancel
            </Button>
            <Button variant="brand" size="lg" type="submit" form="edit-registrant" loading={save.isPending}>
              {ownerChanged ? "Request owner change" : "Save changes"}
            </Button>
          </>
        }
      >
        <Form
          id="edit-registrant"
          className="flex flex-col gap-4"
          error={save.error}
          onSubmit={(e) => {
            e.preventDefault();
            if (complete) save.mutate();
          }}
        >
          <div className="flex flex-col gap-3 sm:flex-row">
            {field("first_name", "First name", { auto: "given-name" })}
            {field("last_name", "Last name", { auto: "family-name" })}
          </div>
          {field("organization_name", "Organization", { optional: true, auto: "organization" })}
          <div className="flex flex-col gap-3 sm:flex-row">
            {field("email", "Email", { type: "email", auto: "email" })}
            <Field size="lg" label="Phone" name="phone" required>
              {(control) => (
                <div className="flex gap-2">
                  <div className="relative w-[78px] shrink-0">
                    <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-fg-subtle">
                      +
                    </span>
                    <input
                      aria-label="Country calling code"
                      inputMode="numeric"
                      value={form.dial}
                      onChange={(e) => set("dial", e.target.value.replace(/\D/g, "").slice(0, 3))}
                      className={`${CONTROL_LG} pl-6`}
                    />
                  </div>
                  <Input
                    size="lg"
                    {...control}
                    type="tel"
                    autoComplete="tel-national"
                    className="min-w-0 flex-1"
                    value={form.number}
                    onChange={(e) => set("number", e.target.value)}
                  />
                </div>
              )}
            </Field>
          </div>
          {field("street", "Street address", { auto: "street-address" })}
          <div className="flex flex-col gap-3 sm:flex-row">
            {field("city", "City", { auto: "address-level2" })}
            {field("state", "State / county", { optional: true, auto: "address-level1" })}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            {field("postcode", "Postcode", { auto: "postal-code" })}
            <Field size="lg" label="Country" name="country" required>
              {(control) => (
                <select
                  {...control}
                  value={form.country}
                  autoComplete="country"
                  onChange={(e) => set("country", e.target.value)}
                  className={CONTROL_LG}
                >
                  {countries.map((c) => (
                    <option key={c} value={c}>
                      {countryName(c)}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
          {ownerChanged ? (
            <Hint tone="warning" title="This changes who owns the domain">
              A new name, organisation or email is a change of registrant. The registrar e-mails an approval
              request to {contact.email} and {emailChanged ? form.email.trim() : "the new owner"}; the change
              is made only once both approve, and the new owner then confirms their email. Your saved contact
              isn&apos;t edited.
            </Hint>
          ) : (
            <Hint tone="success" title="Address and phone changes apply straight away">
              They&apos;re sent to the registry for every domain using these details.
            </Hint>
          )}
          <ErrorAlert error={save.error} />
          <button type="submit" hidden />
        </Form>
      </Modal>
      {unsaved.modal}
    </>
  );
}
